import { Button, Dialog, DialogActions, DialogTitle, Select, Slider, MenuItem, DialogContent } from "@mui/material";
import Spinner from "../spinner";
import React, { useState } from "react";
import type { CameraState, SnapshotFormat, ZoomCaps } from "./camera";
import Camera from "./camera";
import { IGeneratedDocument } from "../../types/document";
import { UtilityService } from "../../services/utilityService";

import '../../styles/cameraDialog.scss';

interface CameraDialogProps {
	closeOnDelivery?: boolean;
	showDialog: boolean;
	snapshotFormat?: SnapshotFormat;
	onDialogCloseRequested: () => void;
	onSnapshotReady: (file: IGeneratedDocument) => void;
}

const CameraDialog: React.FunctionComponent<CameraDialogProps> = ({ closeOnDelivery = true, showDialog, snapshotFormat, onDialogCloseRequested, onSnapshotReady }) => {
	const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[] | null>(null);
	const [zoomCaps, setZoomCaps] = useState<ZoomCaps>({ min: undefined, max: undefined });
	const [zoomLevel, setZoomLevel] = useState<number>(1);
	const [cameraState, setCameraState] = useState<CameraState>("initializing");
	const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState<string | undefined>(undefined);
	const [requestSnapshot, setRequestSnapshot] = useState<boolean>(false);

	const handleCameraStateChanged = (state: CameraState) => {
		setCameraState(state);
	}

	const handleDeviceChanged = (device: MediaDeviceInfo) => {
		setSelectedVideoDeviceId(device.deviceId);
	}

	const handleDevicesAvailable = (devices: MediaDeviceInfo[]) => {
		setVideoDevices(devices);
	}

	const handleErrorRaised = (message: string) => {
		console.error("[CAMERA][ERROR] " + message);
	}

	const handleSnapshotReady = (dataUrl: string) => {
		setRequestSnapshot(false);
		const document = UtilityService.dataUrlToGeneratedDocument(dataUrl, "CameraSnapshot");
		if (document == null) throw new Error("Failed to retrieve snapshot data.");
		onSnapshotReady?.(document);
		if (closeOnDelivery) onDialogCloseRequested?.();
	}

	const handleWarningRaised = (message: string) => {
		console.warn("[CAMERA][WARNING] " + message);
	}

	const handleZoomLevelChanged = (newValue: number) => {
		if (typeof newValue === 'number') {
			// This was causing the slider to jitter while dragging. It was intended to force the 
			// slider to a valid zoom value if an invalid one was sent, but it may not be needed.
			//setZoomLevel(newValue);
		}
	};

	const handleZoomLevelsSupportedChanged = (caps: ZoomCaps) => {
		setZoomCaps(caps);
	}

	return (
		<Dialog
			fullScreen={false}
			open={showDialog}
			className='hwi-camera-dialog'
			maxWidth='sm'
			keepMounted={true}
			fullWidth={true}>
			<DialogTitle id="title">
				<Spinner
					dependency={["initializing", "activating"].includes(cameraState)}
					text={(cameraState == "initializing" ? "Initializing" : "Starting") + ' camera...'}>
					{"Camera"}
				</Spinner>
			</DialogTitle>
			<DialogContent className={cameraState == "enabled" ? "" : "hidden"}>
				<Camera
					enabled={showDialog}
					width={500}
					height={500}
					device={selectedVideoDeviceId}
					snapshotFormat={snapshotFormat}
					requestSnapshot={requestSnapshot}
					zoomLevel={zoomLevel}
					onDevicesAvailable={handleDevicesAvailable}
					onDeviceChanged={handleDeviceChanged}
					onErrorRaised={handleErrorRaised}
					onSnapshotReady={handleSnapshotReady}
					onStateChanged={handleCameraStateChanged}
					onWarningRaised={handleWarningRaised}
					onZoomLevelChanged={handleZoomLevelChanged}
					onZoomLevelsSupportedChanged={handleZoomLevelsSupportedChanged}
				/>
			</DialogContent>
			{cameraState == "enabled" &&
				<DialogActions className="actions-container">
					{(zoomCaps?.min !== undefined && zoomCaps?.max !== undefined) && (
						<div className="zoom">
							<div className="zoom-level">
								<span className="header">Zoom</span><br />
								<span className="data">
									x<span className="value">{zoomLevel.toFixed(1)}</span>
								</span>
							</div>
							<Slider value={zoomLevel} onChange={(_, newValue) => setZoomLevel(newValue as number)} min={zoomCaps.min} max={zoomCaps.max} step={0.1} />
						</div>
					)}
					<div className="devices">
						{videoDevices?.length > 0 &&
							<Select
								value={selectedVideoDeviceId ?? ""}
								onChange={(event) => setSelectedVideoDeviceId(event.target.value)}
								className='camera-select'
							>
								{videoDevices?.map(device => (
									<MenuItem key={device.deviceId} value={device.deviceId}>
										{device.label}
									</MenuItem>
								))}
							</Select>
						}
					</div>
					<div className="actions">
						{/* {allowTorchButton && torch?.isAvailable &&
						<Button
							className={"torch" + (torch?.isOn ? " on" : "")}
							onClick={async () => { if (torch?.isOn) { await torch?.off(); } else { await torch?.on(); } }}
						>
							*
						</Button>
					} */}
						<Button className='capture' color="primary" variant='contained' onClick={() => { setRequestSnapshot(true); }}>
							Capture
						</Button>
						<Button className='cancel' color="error" variant='contained' onClick={() => { onDialogCloseRequested?.(); }}>
							Cancel
						</Button>
					</div>
				</DialogActions>
			}
		</Dialog>
	);
}

export default CameraDialog;